<script lang="ts">
	//Design system components
	import { IconButton, Input } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getErrorOrInfoMessageForMinAndMaxUnit,
		valueOutOfDecimalRange,
	} from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { addNumberStep } from 'src/helpers/number_input_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { VCInterfaceStatus } from 'src/types/VirtualComponents';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device;
	export let status: VCInterfaceStatus;
	export let value: number = 0;
	export let minDecimalDigits: number = null;

	export const save = async (): Promise<SettingRequest> => {
		waitForResponse = true;
		const _value = value;
		const res: SettingRequest = await gen2Request(device, `number.set`, {
			id: status.id,
			value: Number(value),
		});
		if (!res?.isok) {
			targetLock = true;
			value = _value;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
		return res;
	};

	export const valueOutOfRange = (value: number | string): MinMaxInfo => {
		const minMaxInfo = getErrorOrInfoMessageForMinAndMaxUnit(
			value === '' ? NaN : Number(value),
			min,
			max,
			unit,
		);
		if (!minMaxInfo.error && minDecimalDigits !== null) {
			const decimalError = valueOutOfDecimalRange(String(value), minDecimalDigits);
			if (decimalError) minMaxInfo.error = decimalError;
		}
		return minMaxInfo;
	};

	type MinMaxInfo = { error: string; info: string };

	const icon = status.icon || 'fas fa-calculator';
	const min = !isNaN(status?.config?.min) ? status?.config?.min : 0;
	const max = !isNaN(status?.config?.max) ? status?.config?.max : 100;
	const step = status?.config?.meta?.ui?.step || 1;
	const unit = status?.unit || '';

	let minMaxInfo: MinMaxInfo = { error: '', info: '' };
	let targetLock = false;
	let waitForResponse = false;

	/**
	 * Handle user input change
	 * @param {string} val
	 */
	const handleChange = (val: number) => {
		targetLock = true;
		value = val;
	};

	$: if (status) {
		const _value = status?.rawValue ?? status.config?.default_value ?? '';
		if (!waitForResponse && !targetLock) {
			value = _value;
		}
		if (targetLock && value == _value) {
			targetLock = false;
		}
	}

	$: minMaxInfo = valueOutOfRange(value);
</script>

<div class="d-flex mt-s">
	<IconButton
		aria-label={$format('button_decrease', { default: 'Decrease' })}
		icon="fas fa-minus"
		on:click={() => {
			handleChange(addNumberStep(value, -step));
		}}
		disabled={value <= min}
	/>
	<Input
		aria-label={$format('input_set_value', { default: 'Set value' })}
		class="width-md-l width-sm-full ml-s mr-s"
		size="m"
		{icon}
		value={value?.toString() || ''}
		info={minMaxInfo.info}
		error={minMaxInfo.error}
		on:focus={(e) => {
			targetLock = true;
		}}
		on:input={(e) => {
			handleChange(e.target['value']);
		}}
		id="input-34gmgqf"
	/>
	<IconButton
		aria-label={$format('button_increase', { default: 'Increase' })}
		icon="fas fa-plus"
		on:click={() => {
			handleChange(addNumberStep(value, step));
		}}
		disabled={value >= max}
	/>
</div>
